/**
 * Numeo DS primitives — file-private to the SystemConfig admin page. Tailwind-only,
 * dark-mode-aware. Other gluon pages still use MUI Joy; this module is the island of
 * pure DS for the system-config cards.
 *
 * Note on icons: gluon doesn't ship `@gravity-ui/icons` and the surrounding admin rows
 * use `@mui/icons-material`. We accept MUI icons here for visual consistency with sibling
 * row actions — the DS principle is consistent semantic iconography, which MUI satisfies.
 */
import { ReactNode, useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import CloseIcon from '@mui/icons-material/Close'
import SearchIcon from '@mui/icons-material/Search'

// ─── Switch ─────────────────────────────────────────────────────────────────

interface SwitchProps {
    checked: boolean
    onChange: () => void
    disabled?: boolean
    ariaLabel?: string
    /** Visually treat the switch as showing an inherited value, not an explicit one. */
    muted?: boolean
}

export function Switch({ checked, onChange, disabled, ariaLabel, muted }: SwitchProps) {
    const onColor = muted ? 'bg-blue-300 dark:bg-blue-700' : 'bg-blue-600 dark:bg-blue-500'
    const offColor = 'bg-slate-200 dark:bg-slate-700'
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            aria-label={ariaLabel}
            onClick={onChange}
            disabled={disabled}
            className={[
                'relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors',
                'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900',
                'disabled:cursor-not-allowed disabled:opacity-50',
                muted ? 'ring-1 ring-dashed ring-slate-300 dark:ring-slate-600' : '',
                checked ? onColor : offColor,
            ].join(' ')}
        >
            <span
                className={[
                    'inline-block h-5 w-5 transform rounded-full bg-white shadow-sm transition-transform',
                    checked ? 'translate-x-5' : 'translate-x-0.5',
                ].join(' ')}
            />
        </button>
    )
}

// ─── StatusBadge ────────────────────────────────────────────────────────────

type BadgeTone = 'on' | 'off' | 'info' | 'warning' | 'neutral'

interface StatusBadgeProps {
    tone: BadgeTone
    children: ReactNode
    dot?: boolean
}

const BADGE_CLASSES: Record<BadgeTone, string> = {
    on: 'bg-green-50 text-green-700 ring-1 ring-inset ring-green-200 dark:bg-green-900/30 dark:text-green-300 dark:ring-green-700',
    off: 'bg-slate-100 text-slate-700 ring-1 ring-inset ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700',
    info: 'bg-blue-50 text-blue-700 ring-1 ring-inset ring-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:ring-blue-700',
    warning: 'bg-yellow-50 text-yellow-800 ring-1 ring-inset ring-yellow-200 dark:bg-yellow-900/30 dark:text-yellow-300 dark:ring-yellow-700',
    neutral: 'bg-white text-slate-700 ring-1 ring-inset ring-slate-300 dark:bg-slate-900 dark:text-slate-300 dark:ring-slate-600',
}

const BADGE_DOTS: Record<BadgeTone, string> = {
    on: 'bg-green-500',
    off: 'bg-slate-400',
    info: 'bg-blue-500',
    warning: 'bg-yellow-500',
    neutral: 'bg-slate-400',
}

export function StatusBadge({ tone, children, dot }: StatusBadgeProps) {
    return (
        <span className={['inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium', BADGE_CLASSES[tone]].join(' ')}>
            {dot && <span className={['h-1.5 w-1.5 rounded-full', BADGE_DOTS[tone]].join(' ')} />}
            {children}
        </span>
    )
}

// ─── Spinner ────────────────────────────────────────────────────────────────

export function Spinner({ size = 16 }: { size?: number }) {
    return (
        <span
            role="status"
            aria-label="Loading"
            className="inline-block animate-spin rounded-full border-2 border-slate-200 border-t-blue-600 dark:border-slate-700 dark:border-t-blue-400"
            style={{ width: size, height: size }}
        />
    )
}

// ─── Skeleton ───────────────────────────────────────────────────────────────

export function Skeleton({ className = '' }: { className?: string }) {
    return <div className={['animate-pulse rounded bg-slate-200 dark:bg-slate-700', className].join(' ')} />
}

// ─── Avatar ─────────────────────────────────────────────────────────────────

function Avatar({ email, name }: { email?: string; name?: string }) {
    const seed = (name || email || '?').trim()
    const initials =
        seed
            .split(/[\s.@_-]+/)
            .filter(Boolean)
            .slice(0, 2)
            .map((s) => s[0]?.toUpperCase() ?? '')
            .join('') || '?'
    return (
        <div
            aria-hidden="true"
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-slate-200 text-xs font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-200"
        >
            {initials}
        </div>
    )
}

// ─── Modal (portal + ESC + click-outside + scroll lock + focus restore) ─────

interface ModalProps {
    open: boolean
    onClose: () => void
    children: ReactNode
    labelledBy?: string
    /** Tailwind width class. Default max-w-5xl. */
    widthClass?: string
}

function Modal({ open, onClose, children, labelledBy, widthClass = 'max-w-5xl' }: ModalProps) {
    const previousActive = useRef<HTMLElement | null>(null)
    const dialogRef = useRef<HTMLDivElement | null>(null)

    useEffect(() => {
        if (!open) return
        previousActive.current = document.activeElement as HTMLElement | null
        const handleKey = (e: KeyboardEvent): void => {
            if (e.key === 'Escape') {
                e.stopPropagation()
                onClose()
            }
        }
        document.addEventListener('keydown', handleKey)
        const prevOverflow = document.body.style.overflow
        document.body.style.overflow = 'hidden'
        // Move focus into the dialog for screen readers / keyboard nav.
        const focusable = dialogRef.current?.querySelector<HTMLElement>('button, [href], input, [tabindex]:not([tabindex="-1"])')
        focusable?.focus()
        return () => {
            document.removeEventListener('keydown', handleKey)
            document.body.style.overflow = prevOverflow
            previousActive.current?.focus?.()
        }
    }, [open, onClose])

    if (!open) return null

    return createPortal(
        <div
            className="fixed inset-0 z-[1300] flex items-center justify-center bg-black/40 p-4 dark:bg-black/60"
            onMouseDown={(e) => {
                if (e.target === e.currentTarget) onClose()
            }}
        >
            <div
                ref={dialogRef}
                role="dialog"
                aria-modal="true"
                aria-labelledby={labelledBy}
                className={[
                    'flex max-h-[90vh] w-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl',
                    'dark:border-slate-700 dark:bg-slate-900 dark:shadow-slate-950/50',
                    widthClass,
                ].join(' ')}
            >
                {children}
            </div>
        </div>,
        document.body
    )
}

// ─── Close button (icon) ────────────────────────────────────────────────────

function CloseButton({ onClick, label = 'Close' }: { onClick: () => void; label?: string }) {
    return (
        <button
            type="button"
            aria-label={label}
            onClick={onClick}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
        >
            <CloseIcon fontSize="small" />
        </button>
    )
}

// ─── SearchField ────────────────────────────────────────────────────────────

interface SearchFieldProps {
    value: string
    onChange: (v: string) => void
    placeholder?: string
    autoFocus?: boolean
}

function SearchField({ value, onChange, placeholder, autoFocus }: SearchFieldProps) {
    return (
        <div className="relative">
            <span className="pointer-events-none absolute inset-y-0 left-3 flex items-center text-slate-400 dark:text-slate-500">
                <SearchIcon fontSize="small" />
            </span>
            <input
                type="text"
                value={value}
                autoFocus={autoFocus}
                onChange={(e) => onChange(e.target.value)}
                placeholder={placeholder}
                className="block w-full rounded-lg border border-slate-300 bg-white py-2 pl-9 pr-3 text-sm text-slate-900 shadow-sm placeholder:text-slate-400 focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/20 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:placeholder:text-slate-500"
            />
        </div>
    )
}

// ─── FilterTabs (segmented) ─────────────────────────────────────────────────

interface FilterTabOption<T extends string> {
    value: T
    label: string
    count?: number
}

interface FilterTabsProps<T extends string> {
    value: T
    onChange: (v: T) => void
    options: ReadonlyArray<FilterTabOption<T>>
}

function FilterTabs<T extends string>({ value, onChange, options }: FilterTabsProps<T>) {
    return (
        <div
            role="tablist"
            className="inline-flex items-center gap-1 rounded-lg border border-slate-200 bg-slate-50 p-1 dark:border-slate-700 dark:bg-slate-800"
        >
            {options.map((opt) => {
                const active = opt.value === value
                return (
                    <button
                        key={opt.value}
                        type="button"
                        role="tab"
                        aria-selected={active}
                        onClick={() => onChange(opt.value)}
                        className={[
                            'inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                            'focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600',
                            active
                                ? 'bg-white text-slate-900 shadow-sm ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-100 dark:ring-slate-600'
                                : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-slate-200',
                        ].join(' ')}
                    >
                        <span>{opt.label}</span>
                        {typeof opt.count === 'number' && (
                            <span
                                className={[
                                    'inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-[11px] font-semibold',
                                    active
                                        ? 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                                        : 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
                                ].join(' ')}
                            >
                                {opt.count}
                            </span>
                        )}
                    </button>
                )
            })}
        </div>
    )
}

// ─── EmptyState ─────────────────────────────────────────────────────────────

function EmptyState({ icon, title, hint }: { icon?: ReactNode; title: string; hint?: string }) {
    return (
        <div className="flex flex-col items-center justify-center px-6 py-12 text-center">
            {icon && <div className="mb-3 text-slate-400 dark:text-slate-500">{icon}</div>}
            <p className="text-sm font-medium text-slate-900 dark:text-slate-100">{title}</p>
            {hint && <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{hint}</p>}
        </div>
    )
}

// ─── Buttons ────────────────────────────────────────────────────────────────

interface ButtonProps {
    onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
    disabled?: boolean
    children: ReactNode
    type?: 'button' | 'submit'
    size?: 'sm' | 'md'
    variant?: 'primary' | 'secondary' | 'ghost' | 'danger'
    startIcon?: ReactNode
    endIcon?: ReactNode
    fullWidth?: boolean
}

const BUTTON_BASE =
    'inline-flex items-center justify-center gap-2 rounded-lg font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2 dark:focus-visible:ring-offset-slate-900 disabled:opacity-50 disabled:cursor-not-allowed'

const BUTTON_SIZES: Record<NonNullable<ButtonProps['size']>, string> = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-4 py-2 text-sm',
}

const BUTTON_VARIANTS: Record<NonNullable<ButtonProps['variant']>, string> = {
    primary: 'bg-blue-600 text-white hover:bg-blue-700 active:bg-blue-800',
    secondary:
        'border border-slate-300 bg-white text-slate-900 hover:bg-slate-50 active:bg-slate-100 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700',
    ghost: 'bg-transparent text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800',
    danger: 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800',
}

function Button({ onClick, disabled, children, type = 'button', size = 'md', variant = 'primary', startIcon, endIcon, fullWidth }: ButtonProps) {
    return (
        <button
            type={type}
            onClick={onClick}
            disabled={disabled}
            className={[BUTTON_BASE, BUTTON_SIZES[size], BUTTON_VARIANTS[variant], fullWidth ? 'w-full' : ''].join(' ')}
        >
            {startIcon && <span className="flex shrink-0 items-center">{startIcon}</span>}
            {children}
            {endIcon && <span className="flex shrink-0 items-center">{endIcon}</span>}
        </button>
    )
}

// ─── SettingCard ────────────────────────────────────────────────────────────

interface SettingCardProps {
    title: string
    description?: ReactNode
    status?: ReactNode
    accent?: 'primary'
    children: ReactNode
    footer?: ReactNode
}

export function SettingCard({ title, description, status, accent, children, footer }: SettingCardProps) {
    return (
        <section
            className={[
                'overflow-hidden rounded-xl border bg-white shadow-sm dark:bg-slate-900',
                accent === 'primary'
                    ? 'border-blue-200 ring-1 ring-blue-100 dark:border-blue-700/50 dark:ring-blue-900/30'
                    : 'border-slate-200 dark:border-slate-700',
            ].join(' ')}
        >
            <header className="flex items-start justify-between gap-4 px-6 pt-6">
                <div className="space-y-1">
                    <h2 className="text-base font-semibold text-slate-900 dark:text-slate-100">{title}</h2>
                    {description && <p className="max-w-2xl text-sm text-slate-600 dark:text-slate-400">{description}</p>}
                </div>
                {status && <div className="shrink-0">{status}</div>}
            </header>

            <div className="mt-5 border-t border-slate-100 px-6 py-5 dark:border-slate-800">{children}</div>

            {footer && <div className="border-t border-slate-100 bg-slate-50/50 px-6 py-4 dark:border-slate-800 dark:bg-slate-800/30">{footer}</div>}
        </section>
    )
}

// ─── Toast ──────────────────────────────────────────────────────────────────

export type ToastTone = 'success' | 'danger'

interface ToastProps {
    open: boolean
    tone: ToastTone
    message: ReactNode
    onClose: () => void
    /** ms; default 3000 */
    duration?: number
}

export function Toast({ open, tone, message, onClose, duration = 3000 }: ToastProps) {
    useEffect(() => {
        if (!open) return
        const id = setTimeout(onClose, duration)
        return () => clearTimeout(id)
    }, [open, duration, onClose])

    if (!open) return null

    const colors =
        tone === 'success'
            ? 'border-green-200 bg-green-50 text-green-900 dark:border-green-700 dark:bg-green-900/30 dark:text-green-200'
            : 'border-red-200 bg-red-50 text-red-900 dark:border-red-700 dark:bg-red-900/30 dark:text-red-200'

    const Icon = tone === 'success' ? CheckCircleIcon : ErrorOutlineIcon

    return createPortal(
        <div
            role="status"
            aria-live="polite"
            className={[
                'fixed bottom-6 left-1/2 z-[1400] -translate-x-1/2 transform',
                'flex max-w-md items-start gap-3 rounded-lg border px-4 py-3 shadow-lg',
                colors,
            ].join(' ')}
        >
            <span className="shrink-0 pt-0.5">
                <Icon fontSize="small" />
            </span>
            <span className="text-sm">{message}</span>
            <button
                type="button"
                aria-label="Dismiss"
                onClick={onClose}
                className="ml-2 shrink-0 rounded p-0.5 opacity-70 hover:bg-black/5 hover:opacity-100 dark:hover:bg-white/10"
            >
                <CloseIcon fontSize="small" />
            </button>
        </div>,
        document.body
    )
}
